import React, { useEffect, useState } from "react";
import store from "store2";
import { useSiteVisitation } from "../../../providers/SiteVisitationProvider";

const FlavorPrices = ({
  values,
  setFieldValue,
  flavors,
  jobId,
  disabled = false,
}: {
  values: any;
  setFieldValue: any;
  flavors: any[];
  disabled?: boolean;
  jobId: any;
}) => {
  const [enabledFlavors, setEnabledFlavors] = useState<{ [key: string]: boolean }>({});
  const {saveLocationFlavorPriceToStore, logJobBasic} = useSiteVisitation();
  const handleFlavorPriceChange = (flavorKey: string, value: string) => {
    const newPrices = (values.flavorPrices || []).map((item: any) =>
      item.flavor === flavorKey
        ? { ...item, price: value, is_active: enabledFlavors[flavorKey] }
        : item
    );
    setFieldValue("flavorPrices", newPrices);
    saveLocationFlavorPriceToStore(jobId, newPrices);
  };

  const handleCheckboxChange = (flavorKey: string) => {
    const updated = {
      ...enabledFlavors,
      [flavorKey]: !enabledFlavors[flavorKey],
    };
    setEnabledFlavors(updated);

    const newPrices = (values.flavorPrices || []).map((item: any) =>
      item.flavor === flavorKey
        ? { ...item, is_active: !enabledFlavors[flavorKey], price: item.price }
        : item
    );
    setFieldValue("flavorPrices", newPrices);
    saveLocationFlavorPriceToStore(jobId, newPrices);
  };

  // Set default pricing
  const setDefaultPricing = () => {
    const defaultPrices = flavors.map((flavor: any) => {
      const prev = (values.flavorPrices || []).find((item: any) => item.flavor === flavor.flavor);
      const price = flavor.defaultPrice ?? 0;
      return {
        flavor: flavor.flavor,
        price: String(price),
        is_active: prev?.is_active ?? true,
      };
    });
    setFieldValue("flavorPrices", defaultPrices);
    setEnabledFlavors(
      Object.fromEntries(defaultPrices.map((item: any) => [item.flavor, item.is_active]))
    );
    saveLocationFlavorPriceToStore(jobId, defaultPrices);
  };

  // Initialize flavor prices and enabled states
  useEffect(() => {
    if (!flavors || flavors.length === 0 || !logJobBasic) return;

    const jobData = store.get("job_id_data") || {};
    const jobSpecificData = jobData[jobId] || {};
    const storedPrices = jobSpecificData.location?.flavorPrices;

    if (storedPrices && storedPrices.length > 0) {
      setFieldValue("flavorPrices", storedPrices);
      setEnabledFlavors(
        Object.fromEntries(storedPrices.map((item: any) => [item.flavor, !!item.is_active]))
      );
    } else if (!values.flavorPrices || values.flavorPrices.length === 0) {
      // Fallback to logJob or flavors
      const logPrices = flavors.map((flavor: any) => {
        const existing = logJobBasic.flavorPrices?.find((item: any) => item.flavor === flavor.flavor);
        return {
          flavor: flavor.flavor,
          price: existing?.price?.toString() ?? String(flavor.defaultPrice ?? 0),
          is_active: existing?.isActive ?? true,
        };
      });
      setFieldValue("flavorPrices", logPrices);
      setEnabledFlavors(
        Object.fromEntries(logPrices.map((item: any) => [item.flavor, !!item.is_active]))
      );
      saveLocationFlavorPriceToStore(jobId, logPrices);
    }
    // eslint-disable-next-line
  }, [flavors, logJobBasic, setFieldValue, jobId]);

  return (
    <div className="accordion mt-4" id="flavorPricesAccordion">
      <div className="accordion-item">
        <h2 className="accordion-header" id="flavorPricesHeading">
          <button
            className="accordion-button collapsed py-5 fs-4 fw-bold bg-light"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#flavorPricesCollapse"
            aria-expanded="false"
            aria-controls="flavorPricesCollapse"
            title="Click here to add marketing purpose"
          >
            Flavor Price
          </button>
        </h2>
        <div
          id="flavorPricesCollapse"
          className="accordion-collapse collapse"
          aria-labelledby="flavorPricesHeading"
          data-bs-parent="#flavorPricesAccordion"
        >
          <div className="accordion-body">
            <div className="row">
              <div>
                <button
                  type="button"
                  className="btn btn-sm btn-primary float-end"
                  onClick={setDefaultPricing}
                  disabled={disabled}
                >
                  Default Pricing
                </button>
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th className="fw-bold mb-0">Flavor</th>
                      <th className="fw-bold mb-0">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(values.flavorPrices || []).map((flavor: any) => {
                      const flavorKey = flavor.flavor;
                      const currentFlavor = flavors?.find(
                        (item: any) => item.flavor === flavorKey
                      );
                      const isEnabled = enabledFlavors[flavorKey] ?? true;
                      return (
                        <tr key={flavorKey}>
                          <td className="align-middle">
                            <input
                              className="form-check-input mb-2 me-2"
                              type="checkbox"
                              checked={isEnabled}
                              onChange={() => handleCheckboxChange(flavorKey)}
                              disabled={disabled}
                            />
                            {currentFlavor?.name || flavorKey}
                          </td>
                          <td className="align-middle">
                            <input
                              type="number"
                              name={`flavorPrices.${flavorKey}`}
                              id={`flavorPrices_${flavorKey}`}
                              className="form-control mb-1"
                              value={flavor?.price ?? ""}
                              min={0}
                              onChange={(e) =>
                                handleFlavorPriceChange(flavorKey, e.target.value)
                              }
                              disabled={!isEnabled || disabled}
                              placeholder={`Default: ${currentFlavor?.defaultPrice ?? "-"}`}
                              step="any"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlavorPrices;